import { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import {
  Zap,
  Play,
  Calendar,
  Webhook,
  Phone,
  MessageSquare,
  Users,
  FileText,
  Tag,
  GitBranch,
  Split,
  RotateCcw,
  Clock,
  AlertTriangle,
  Variable,
  ArrowRight,
  Globe,
  Mail,
  Bell,
  Shuffle,
  FileCode,
  Bot,
  Brain,
  Layers,
  Server,
  Wrench,
  Send,
  Contact,
  PhoneCall,
  Database,
} from 'lucide-react';

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  play: Play,
  calendar: Calendar,
  webhook: Webhook,
  phone: Phone,
  'phone-incoming': Phone,
  'phone-missed': Phone,
  voicemail: MessageSquare,
  'message-square': MessageSquare,
  users: Users,
  'user-plus': Users,
  'file-text': FileText,
  tag: Tag,
  'git-branch': GitBranch,
  split: Split,
  'rotate-ccw': RotateCcw,
  clock: Clock,
  'alert-triangle': AlertTriangle,
  variable: Variable,
  'arrow-right': ArrowRight,
  globe: Globe,
  mail: Mail,
  bell: Bell,
  shuffle: Shuffle,
  'file-code': FileCode,
  bot: Bot,
  brain: Brain,
  layers: Layers,
  zap: Zap,
  server: Server,
  wrench: Wrench,
  send: Send,
  contact: Contact,
  'phone-call': PhoneCall,
  database: Database,
};

interface WorkflowNodeData {
  label: string;
  nodeType: string;
  config: Record<string, unknown>;
  icon?: string;
  category?: string;
  mcp_server_name?: string;
}

const CATEGORY_THEME: Record<string, { bg: string; border: string; ring: string; badge: string; label: string; shadow: string }> = {
  triggers: {
    bg: 'rgba(6, 182, 212, 0.2)',
    border: 'rgba(6, 182, 212, 0.5)',
    ring: 'ring-cyan-400',
    badge: 'bg-cyan-500',
    label: 'text-cyan-300/70',
    shadow: 'shadow-cyan-500/30',
  },
  flow_control: {
    bg: 'rgba(168, 85, 247, 0.2)',
    border: 'rgba(168, 85, 247, 0.5)',
    ring: 'ring-purple-400',
    badge: 'bg-purple-500',
    label: 'text-purple-300/70',
    shadow: 'shadow-purple-500/30',
  },
  utilities: {
    bg: 'rgba(34, 197, 94, 0.2)',
    border: 'rgba(34, 197, 94, 0.5)',
    ring: 'ring-green-400',
    badge: 'bg-green-500',
    label: 'text-green-300/70',
    shadow: 'shadow-green-500/30',
  },
  messaging: {
    bg: 'rgba(59, 130, 246, 0.2)',
    border: 'rgba(59, 130, 246, 0.5)',
    ring: 'ring-blue-400',
    badge: 'bg-blue-500',
    label: 'text-blue-300/70',
    shadow: 'shadow-blue-500/30',
  },
  crm: {
    bg: 'rgba(249, 115, 22, 0.2)',
    border: 'rgba(249, 115, 22, 0.5)',
    ring: 'ring-orange-400',
    badge: 'bg-orange-500',
    label: 'text-orange-300/70',
    shadow: 'shadow-orange-500/30',
  },
  ai: {
    bg: 'rgba(139, 92, 246, 0.2)',
    border: 'rgba(139, 92, 246, 0.5)',
    ring: 'ring-violet-400',
    badge: 'bg-violet-500',
    label: 'text-violet-300/70',
    shadow: 'shadow-violet-500/30',
  },
  video_call: {
    bg: 'rgba(99, 102, 241, 0.2)',
    border: 'rgba(99, 102, 241, 0.5)',
    ring: 'ring-indigo-400',
    badge: 'bg-indigo-500',
    label: 'text-indigo-300/70',
    shadow: 'shadow-indigo-500/30',
  },
  supplychain: {
    bg: 'rgba(16, 185, 129, 0.2)',
    border: 'rgba(16, 185, 129, 0.5)',
    ring: 'ring-emerald-400',
    badge: 'bg-emerald-500',
    label: 'text-emerald-300/70',
    shadow: 'shadow-emerald-500/30',
  },
  hr: {
    bg: 'rgba(244, 63, 94, 0.2)',
    border: 'rgba(244, 63, 94, 0.5)',
    ring: 'ring-rose-400',
    badge: 'bg-rose-500',
    label: 'text-rose-300/70',
    shadow: 'shadow-rose-500/30',
  },
};

const CATEGORY_LABELS: Record<string, string> = {
  triggers: 'Trigger',
  flow_control: 'Controllo Flusso',
  utilities: 'Utilità',
  video_call: 'Video Call',
  messaging: 'Messaggistica',
  crm: 'CRM',
  ai: 'AI',
  supplychain: 'Supply Chain & Catalogo',
  hr: 'Risorse Umane',
};

export const TriggerNode = memo(({ data, selected }: NodeProps<WorkflowNodeData>) => {
  const Icon = ICON_MAP[data.icon || 'zap'] || Zap;

  return (
    <div
      className={`px-4 py-3 rounded-xl shadow-lg min-w-[180px] transition-all ${
        selected
          ? 'ring-2 ring-cyan-400 ring-offset-2 ring-offset-gray-900'
          : 'hover:shadow-xl'
      }`}
      style={{
        background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.2) 0%, rgba(6, 182, 212, 0.1) 100%)',
        border: '2px solid rgba(6, 182, 212, 0.5)',
        backdropFilter: 'blur(8px)',
      }}
    >
      <Handle
        type="source"
        position={Position.Right}
        className="!bg-cyan-400 !w-3 !h-3 !border-2 !border-white"
      />
      <div className="flex items-center gap-3">
        <div className="p-2 bg-cyan-500 rounded-lg shadow-lg shadow-cyan-500/30">
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div>
          <div className="font-semibold text-white text-sm">{data.label}</div>
          <div className="text-[10px] text-cyan-300/70">Trigger</div>
        </div>
      </div>
    </div>
  );
});

TriggerNode.displayName = 'TriggerNode';

export const ActionNode = memo(({ data, selected }: NodeProps<WorkflowNodeData>) => {
  const Icon = ICON_MAP[data.icon || 'wrench'] || Wrench;
  const theme = CATEGORY_THEME[data.category || 'utilities'] || CATEGORY_THEME.utilities;
  const categoryLabel = CATEGORY_LABELS[data.category || 'utilities'] || 'Azione';

  return (
    <div
      className={`px-4 py-3 rounded-xl shadow-lg min-w-[180px] transition-all ${
        selected
          ? `ring-2 ${theme!.ring!} ring-offset-2 ring-offset-gray-900`
          : 'hover:shadow-xl'
      }`}
      style={{
        background: `linear-gradient(135deg, ${theme!.bg!} 0%, ${theme!.bg.replace('0.2', '0.1')!} 100%)`,
        border: `2px solid ${theme!.border!}`,
        backdropFilter: 'blur(8px)',
      }}
    >
      <Handle
        type="target"
        position={Position.Left}
        className="!w-3 !h-3 !border-2 !border-white"
        style={{ backgroundColor: theme!.border! }}
      />
      <Handle
        type="source"
        position={Position.Right}
        className="!w-3 !h-3 !border-2 !border-white"
        style={{ backgroundColor: theme!.border! }}
      />
      <div className="flex items-center gap-3">
        <div className={`p-2 ${theme!.badge!} rounded-lg shadow-lg ${theme!.shadow!}`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div>
          <div className="font-semibold text-white text-sm">{data.label}</div>
          <div className={`text-[10px] ${theme!.label!}`}>{categoryLabel}</div>
        </div>
      </div>
    </div>
  );
});

ActionNode.displayName = 'ActionNode';

export const FlowControlNode = memo(({ data, selected }: NodeProps<WorkflowNodeData>) => {
  const Icon = ICON_MAP[data.icon || 'git-branch'] || GitBranch;
  const isConditional = ['if_condition', 'switch_case'].includes(data.nodeType);

  return (
    <div
      className={`px-4 py-3 rounded-xl shadow-lg min-w-[180px] transition-all ${
        selected
          ? 'ring-2 ring-purple-400 ring-offset-2 ring-offset-gray-900'
          : 'hover:shadow-xl'
      }`}
      style={{
        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(139, 92, 246, 0.1) 100%)',
        border: '2px solid rgba(139, 92, 246, 0.5)',
        backdropFilter: 'blur(8px)',
      }}
    >
      <Handle
        type="target"
        position={Position.Left}
        className="!bg-purple-400 !w-3 !h-3 !border-2 !border-white"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="default"
        className="!bg-purple-400 !w-3 !h-3 !border-2 !border-white"
        style={{ top: isConditional ? '30%' : '50%' }}
      />
      {isConditional && (
        <>
          <Handle
            type="source"
            position={Position.Right}
            id="true"
            className="!bg-green-400 !w-3 !h-3 !border-2 !border-white"
            style={{ top: '30%' }}
          />
          <Handle
            type="source"
            position={Position.Right}
            id="false"
            className="!bg-red-400 !w-3 !h-3 !border-2 !border-white"
            style={{ top: '70%' }}
          />
        </>
      )}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-purple-500 rounded-lg shadow-lg shadow-purple-500/30">
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div>
          <div className="font-semibold text-white text-sm">{data.label}</div>
          <div className="text-[10px] text-purple-300/70">Flusso</div>
        </div>
      </div>
    </div>
  );
});

FlowControlNode.displayName = 'FlowControlNode';

export const MCPNode = memo(({ data, selected }: NodeProps<WorkflowNodeData>) => {
  const Icon = ICON_MAP[data.icon || 'server'] || Server;

  return (
    <div
      className={`px-4 py-3 rounded-xl shadow-lg min-w-[180px] transition-all ${
        selected
          ? 'ring-2 ring-amber-400 ring-offset-2 ring-offset-gray-900'
          : 'hover:shadow-xl'
      }`}
      style={{
        background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(245, 158, 11, 0.1) 100%)',
        border: '2px solid rgba(245, 158, 11, 0.5)',
        backdropFilter: 'blur(8px)',
      }}
    >
      <Handle
        type="target"
        position={Position.Left}
        className="!bg-amber-400 !w-3 !h-3 !border-2 !border-white"
      />
      <Handle
        type="source"
        position={Position.Right}
        className="!bg-amber-400 !w-3 !h-3 !border-2 !border-white"
      />
      <div className="flex items-center gap-3">
        <div className="p-2 bg-amber-500 rounded-lg shadow-lg shadow-amber-500/30">
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div>
          <div className="font-semibold text-white text-sm">{data.label}</div>
          <div className="text-[10px] text-amber-300/70 flex items-center gap-1">
            <Server className="h-2.5 w-2.5" />
            {data.mcp_server_name || 'MCP'}
          </div>
        </div>
      </div>
    </div>
  );
});

MCPNode.displayName = 'MCPNode';
